import React from 'react';
import styled from 'styled-components';
import { easeEnter } from './motion';

const CARD_SHADOW = '0 18px 40px -18px rgba(28, 28, 28, 0.35)';
const CHROME_BG = '#e8e6e4';
const CHROME_LINE = '#d9d6d3';
const CHROME_TEXT = '#6b6865';

/* Each card is one step of the workflow the band below converges — tagged so
   the mess reads as "software failing the job", not just clutter. */
type MessSpec = {
  left: number;
  top: number;
  width: number;
  rot: number;
  z: number;
  dx: number;
  dy: number;
};

const MESS_LAYOUT: MessSpec[] = [
  { left: 0, top: 96, width: 266, rot: -4, z: 1, dx: -46, dy: 34 },
  { left: 74, top: 18, width: 242, rot: 3, z: 2, dx: 38, dy: -46 },
  { left: 148, top: 122, width: 184, rot: 5, z: 3, dx: 44, dy: -20 },
  { left: 212, top: 232, width: 112, rot: 8, z: 4, dx: 66, dy: 26 },
  { left: 30, top: 336, width: 224, rot: -3, z: 5, dx: -28, dy: 56 },
  { left: -8, top: 2, width: 204, rot: -6, z: 6, dx: -56, dy: -26 },
];

const StackRoot = styled.div`
  position: relative;
  width: 332px;
  height: 412px;
  filter: grayscale(1) contrast(0.92);
`;

/* Timeline transform lives on the shell; hover lift lives on the inner card
   so the two never fight over the same transform property. */
const CardShell = styled.div`
  position: absolute;
  will-change: transform, opacity;

  .mess-card {
    transition: transform 320ms cubic-bezier(0.22, 0.9, 0.24, 1);
  }

  &:hover .mess-card {
    transform: translateY(-5px);
  }
`;

const ChromeCard = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid ${CHROME_LINE};
  border-radius: 8px;
  box-shadow: ${CARD_SHADOW};
  overflow: hidden;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: ${CHROME_BG};
  border-bottom: 1px solid ${CHROME_LINE};
  font-size: 9px;
  letter-spacing: 0.02em;
  color: ${CHROME_TEXT};
  white-space: nowrap;
`;

const WindowDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cfccc9;
`;

const FxBar = styled.div`
  padding: 5px 8px;
  border-bottom: 1px solid ${CHROME_LINE};
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 8.5px;
  color: ${CHROME_TEXT};
  white-space: nowrap;
  overflow: hidden;
`;

const SheetHead = styled.div`
  display: flex;
  background: #f3f2f0;
  border-bottom: 1px solid ${CHROME_LINE};
`;

const SheetHeadCell = styled.span`
  flex: 1;
  text-align: center;
  padding: 2px 0;
  font-size: 8.5px;
  color: #8a8784;
  border-right: 1px solid ${CHROME_LINE};

  &:last-child {
    border-right: none;
  }
`;

const SheetGrid = styled.div`
  position: relative;
  height: 118px;
  background:
    repeating-linear-gradient(to right, transparent 0 43px, #eceae8 43px 44px),
    repeating-linear-gradient(to bottom, transparent 0 23px, #eceae8 23px 24px);
`;

const CellError = styled.span`
  position: absolute;
  font-size: 9px;
  font-weight: 700;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  color: #c0392b;
`;

const SelectedCell = styled.span`
  position: absolute;
  width: 44px;
  height: 24px;
  border: 1.5px solid #4a72b0;
`;

const ErpBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px;
`;

const ErpRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErpLabel = styled.span`
  width: 54px;
  height: 8px;
  border-radius: 2px;
  background: #dcd9d6;
`;

const ErpInput = styled.span`
  flex: 1;
  height: 16px;
  border: 1px solid #cfccc9;
  border-radius: 3px;
  background: #fafaf9;
`;

const ErpFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErpButton = styled.span`
  padding: 4px 12px;
  border-radius: 3px;
  background: #cfccc9;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #55524f;
`;

const ErpStatus = styled.span`
  margin-left: auto;
  font-size: 9px;
  font-style: italic;
  color: #8a8784;
`;

const Note = styled.div`
  position: relative;
  height: 112px;
  padding: 12px 10px;
  background: #f7f3cf;
  box-shadow: ${CARD_SHADOW};
  font-family: 'Segoe Print', 'Bradley Hand', 'Comic Sans MS', cursive;
  font-size: 11.5px;
  line-height: 1.45;
  color: #4a4636;
`;

const FileChip = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 11px;
  background: #ffffff;
  border: 1px solid ${CHROME_LINE};
  border-radius: 8px;
  box-shadow: ${CARD_SHADOW};
`;

const FileIcon = styled.span`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: #217346;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
`;

const FileName = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 10px;
  color: #4a4a4a;
  white-space: nowrap;
`;

const Toast = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 11px;
  background: #ffffff;
  border: 1px solid ${CHROME_LINE};
  border-radius: 8px;
  box-shadow: ${CARD_SHADOW};
  font-size: 10px;
  color: #44413e;
`;

const ToastIcon = styled.span`
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #c0392b;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
`;

const TagLabel = styled.span`
  position: absolute;
  top: 6px;
  right: 7px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #a8a4a0;
`;

const PaperTag = styled.span`
  position: absolute;
  top: -9px;
  left: 12px;
  padding: 2px 8px;
  background: #ffffff;
  border: 1px solid ${CHROME_LINE};
  border-radius: 999px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #8a8784;
`;

const MailCard = styled.div`
  position: relative;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid ${CHROME_LINE};
  border-radius: 8px;
  box-shadow: ${CARD_SHADOW};
`;

const MailFrom = styled.div`
  font-size: 9px;
  color: #8a8784;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MailSubject = styled.div`
  margin-top: 3px;
  font-size: 10.5px;
  font-weight: 700;
  color: #44413e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MailAttach = styled.div`
  margin-top: 7px;
  padding: 5px 8px;
  border: 1px dashed #cfccc9;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 8.5px;
  color: #6b6865;
  white-space: nowrap;
  overflow: hidden;
`;

const MESS_CONTENT: React.ReactNode[] = [
  <ChromeCard>
    <TagLabel>BUILD</TagLabel>
    <TitleBar>
      <WindowDot />
      <WindowDot />
      <WindowDot />
      Book1 — Microsoft Excel (compatibility mode)
    </TitleBar>
    <FxBar>fx&nbsp;&nbsp;=VLOOKUP(A2,&apos;2019 Q3 copy (final)&apos;!A:Z,14,FALSE)</FxBar>
    <SheetHead>
      {['A', 'B', 'C', 'D', 'E', 'F'].map((letter) => (
        <SheetHeadCell key={letter}>{letter}</SheetHeadCell>
      ))}
    </SheetHead>
    <SheetGrid>
      <SelectedCell style={{ left: 44, top: 24 }} />
      <CellError style={{ left: 52, top: 30 }}>#REF!</CellError>
      <CellError style={{ left: 134, top: 54 }}>####</CellError>
      <CellError style={{ left: 180, top: 78 }}>#DIV/0!</CellError>
    </SheetGrid>
  </ChromeCard>,
  <ChromeCard>
    <TagLabel>RUN</TagLabel>
    <TitleBar>
      <WindowDot />
      <WindowDot />
      <WindowDot />
      SalesOrder.exe — LEGACY-ERP v4.2
    </TitleBar>
    <ErpBody>
      <ErpRow>
        <ErpLabel />
        <ErpInput />
      </ErpRow>
      <ErpRow>
        <ErpLabel />
        <ErpInput />
      </ErpRow>
      <ErpRow>
        <ErpLabel />
        <ErpInput />
      </ErpRow>
      <ErpFooter>
        <ErpButton>SUBMIT</ErpButton>
        <ErpStatus>Not responding…</ErpStatus>
      </ErpFooter>
    </ErpBody>
  </ChromeCard>,
  <MailCard>
    <PaperTag>INTAKE</PaperTag>
    <MailFrom>From: client@acme.co</MailFrom>
    <MailSubject>RE: RE: RE: order form??</MailSubject>
    <MailAttach>order_form_FINAL(3).pdf</MailAttach>
  </MailCard>,
  <Note>
    <PaperTag>GROW</PaperTag>
    FIX BEFORE
    <br />
    FRIDAY!!
    <br />
    — mgmt
  </Note>,
  <FileChip>
    <PaperTag>SHIP</PaperTag>
    <FileIcon>X</FileIcon>
    <FileName>Report_FINAL_v7 (2).xlsx</FileName>
  </FileChip>,
  <Toast>
    <ToastIcon>✕</ToastIcon>
    Circular reference detected
  </Toast>,
];

interface MessStackProps {
  /** Per-card 0..1 tumble-in progress. */
  progress: number[];
}

export const MessStack: React.FC<MessStackProps> = ({ progress }) => (
  <StackRoot aria-hidden="true">
    {MESS_LAYOUT.map((spec, i) => {
      const entered = easeEnter(progress[i] ?? 1);
      const drift = 1 - entered;
      return (
        <CardShell
          key={i}
          style={{
            left: spec.left,
            top: spec.top,
            width: spec.width,
            zIndex: spec.z,
            opacity: entered,
            transform:
              `translate(${spec.dx * drift}px, ${spec.dy * drift}px) ` +
              `rotate(${spec.rot + spec.rot * 1.8 * drift}deg)`,
          }}
        >
          <div className="mess-card">{MESS_CONTENT[i]}</div>
        </CardShell>
      );
    })}
  </StackRoot>
);
